import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const umaEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}, {
  label: 'Graduate Degree',
  value: '8'
}];
const umaStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const umaPrograms = [{
  slug: 'healthcare-accounting',
  title: 'Associate of Science (A.S.) in Healthcare Accounting',
  shortTitle: 'Healthcare Accounting',
  mProgram: '45236'
}, {
  slug: 'healthcare-management',
  title: 'Associate of Science (A.S.) in Healthcare Management',
  shortTitle: 'Healthcare Management',
  mProgram: '45232'
}, {
  slug: 'health-and-human-services',
  title: 'Associate of Science (A.S.) in Health & Human Services',
  shortTitle: 'Health & Human Services',
  mProgram: '45235'
}, {
  slug: 'health-information-management',
  title: 'Associate of Science (A.S.) in Health Information Management',
  shortTitle: 'Health Information Management',
  mProgram: '45233'
}, {
  slug: 'medical-administrative-assistant',
  title: 'Associate of Science (A.S.) in Medical Administrative Assistant',
  shortTitle: 'Medical Administrative Assistant',
  mProgram: '45234'
}, {
  slug: 'medical-billing-and-coding',
  title: 'Associate of Science (A.S.) in Medical Billing & Coding',
  shortTitle: 'Medical Billing & Coding',
  mProgram: '45229'
}, {
  slug: 'medical-office-and-billing-specialist',
  title: 'Associate of Science (A.S.) in Medical Office & Billing Specialist',
  shortTitle: 'Medical Office & Billing Specialist',
  mProgram: '45230'
}];
export function UMALandingPage() {
  usePageMeta('Ultimate Medical Academy Online | Healthcare Career Training Programs', 'Explore online healthcare degree programs at Ultimate Medical Academy. Get personalized support, financial aid options, and job search assistance. Request free info today.', {
    ogImage: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = umaPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7uetpg/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Ultimate Medical Academy',
            logo: 'https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp',
            primaryColor: '#003B5C',
            accentColor: '#00BFA5',
            backUrl: '/colleges/ultimate-medical-academy',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const currentProgramTitle = umaPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1284">
          <div data-id="element-1285">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1286">
              Select a Program
            </label>
            <div className="relative" data-id="element-1287">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#4ECDC4] focus:border-[#4ECDC4] font-medium text-gray-800" data-id="element-1288">
                <option value="" data-id="element-1289">-- Choose a Program --</option>
                {umaPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1290">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1291">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1292" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-1293">
            Select a program above to get started
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1294">
          <div data-id="element-1295">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1296">
              Program
            </label>
            <div className="relative" data-id="element-1297">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1298">
                {umaPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1299">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1300">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1301" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1302" />

          <div data-id="element-1303">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1304">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1305">
              <option value="" data-id="element-1306">-- Please Select --</option>
              {umaEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1307">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1308">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1309">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1310">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1311">
              <option value="" data-id="element-1312">-- Please Select --</option>
              {umaStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1313">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1314">{errors.startDate}</p>}
          </div>

          <div data-id="element-1315">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1316">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1317">
              <option value="" data-id="element-1318">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1319">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1320">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1321">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1322">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1323" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1324">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1325">
          <div data-id="element-1326">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1327">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1328" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1329">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1330">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1331" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1332">
                <div data-id="element-1333">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1334">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="City" data-id="element-1335" />
                </div>
                <div data-id="element-1336">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1337">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="State" data-id="element-1338" />
                </div>
              </div>
              <div data-id="element-1339">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1340">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`} placeholder="123 Main St" data-id="element-1341" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1342">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1343">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1344">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1345" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1346">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1347">
          <div className="grid grid-cols-2 gap-3" data-id="element-1348">
            <div data-id="element-1349">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1350">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1351" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1352">{errors.firstName}</p>}
            </div>
            <div data-id="element-1353">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1354">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1355" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1356">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1357">
            <div data-id="element-1358">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1359">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1360" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1361">{errors.email}</p>}
            </div>
            <div data-id="element-1362">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1363">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1364" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1365">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2 pt-2" data-id="element-1366">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-1367">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#00BFA5] focus:ring-[#00BFA5] cursor-pointer flex-shrink-0" data-id="element-1368" />
              <span className="text-[10px] text-gray-500 leading-tight" data-id="element-1369">
                By clicking the Request Info button, you agree to be contacted
                by phone, text message, or email via automated systems by
                Ultimate Medical Academy about your education at the phone
                numbers you provided above, including any wireless number(s).
                You are not obligated to agree to automated contact to enroll.
                Note that even non-automated calls are recorded for quality
                assurance.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-1370">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1371">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1372">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1373" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1374">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1375" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1376">
      {/* A. Custom Header */}
      <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1377">
        <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-16 object-contain" data-id="element-1378" />
      </header>

      {/* B. Hero Section */}
      <section className="bg-gradient-to-r from-[#176B9C] to-[#003B5C] relative overflow-hidden" data-id="element-1379">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16" data-id="element-1380">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-1381">
            <p className="text-[#4ECDC4] font-medium mb-3 text-lg tracking-wide" data-id="element-1382">
              Ultimate Medical Academy Online<sup className="text-xs" data-id="element-1383">1</sup>
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-1384">
              {umaPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Healthcare
                  <br data-id="element-1385" />
                  Career Training
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-1386">
              UMA students get personalized attention throughout the entire
              education journey. Our students get support from the very
              beginning, throughout career training, and after graduation.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-1387">
            <form onSubmit={e => e.preventDefault()} id="uma-lead-form" data-id="element-1388">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1389" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1390" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#003B5C] mb-4" data-id="element-1391">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1392">
                <div className="bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1393"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1394">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1395">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-[#F4F9FB] py-12 border-b border-gray-200" data-id="element-1396">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1397">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center" data-id="element-1398">
            {[{
            icon: BookOpenIcon,
            title: 'One-on-one\nAcademic Support'
          }, {
            icon: AwardIcon,
            title: 'Certification Prep²',
            sup: '2'
          }, {
            icon: MonitorIcon,
            title: '24/7 Technical\nSupport'
          }, {
            icon: ClipboardCheckIcon,
            title: "Apply Degree Credits\nToward a Bachelor's³",
            sup: '3'
          }, {
            icon: BriefcaseIcon,
            title: 'Job Search\nAssistance'
          }, {
            icon: LaptopIcon,
            title: 'Laptops for those\nwho qualify⁴',
            sup: '4'
          }].map((item, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-1399">
                <item.icon className="w-10 h-10 text-[#176B9C] mb-3 stroke-[1.5]" data-id="element-1400" />
                <p className="text-xs font-bold text-[#176B9C] whitespace-pre-line leading-tight" data-id="element-1401">
                  {item.title.replace(/²|³|⁴/g, '')}
                  {item.sup && <sup className="text-[10px]" data-id="element-1402">{item.sup}</sup>}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* D. Three Feature Cards */}
      <section className="py-16 bg-white" data-id="element-1403">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1404">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1405">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/dFP2Bkreu2BvfkWxRv6Va3/Image_1.webp',
            title: 'Financial Aid',
            desc: 'Let us help you explore options for financial aid. We will help you through the process to see if you qualify.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/9FrwRXe1BYLuSp6GRYweVA/image_2.webp',
            title: 'Job Search Assistance',
            desc: 'Our Career Service team helps graduates pursue job opportunities, schedule interviews, and more.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qcGrjYfSLHXUMihbDJMyJz/image_3.webp',
            title: 'Engaging Online Learning',
            desc: 'Get individual instructor feedback, personalized advisor support, and interact with peers on discussion posts.'
          }].map((card, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-1406">
                <img src={card.img} alt={card.title} className="w-full h-48 object-cover" data-id="element-1407" />
                <div className="p-6 flex-1" data-id="element-1408">
                  <h3 className="text-xl font-bold text-[#003B5C] mb-3" data-id="element-1409">
                    {card.title}
                  </h3>
                  <p className="text-sm text-[#176B9C] font-medium leading-relaxed" data-id="element-1410">
                    {card.desc}
                  </p>
                </div>
              </div>)}
          </div>
          <div className="text-center" data-id="element-1411">
            <button onClick={scrollToForm} className="inline-block bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] text-white font-bold py-3 px-12 rounded-full hover:shadow-lg transition-all" data-id="element-1412">
              Take the Next Step
            </button>
          </div>
        </div>
      </section>

      {/* E. '4 out of 5' Stat Section */}
      <section className="bg-[#F4F9FB] py-16" data-id="element-1413">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-1414">
          <div className="text-center md:text-right max-w-xs" data-id="element-1415">
            <div className="text-[#00BFA5] font-bold text-5xl sm:text-6xl mb-2" data-id="element-1416">
              4 <span className="text-[#176B9C]" data-id="element-1417">out of 5</span>
            </div>
            <p className="text-[#176B9C] text-sm font-medium" data-id="element-1418">
              surveyed employers say they are likely to hire a UMA graduate
              again.⁵
            </p>
          </div>
          <div className="flex gap-2 sm:gap-4" data-id="element-1419">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-white" data-id="element-1420">
                <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#00BFA5]" data-id="element-1421" />
              </div>)}
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-transparent" data-id="element-1422">
              <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#D8E8F0]" data-id="element-1423" />
            </div>
          </div>
        </div>
      </section>

      {/* F. 25% Georgetown Study Section */}
      <section className="py-16 bg-white" data-id="element-1424">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1425">
          <div className="bg-[#003B5C] rounded-2xl overflow-hidden relative flex flex-col md:flex-row items-center shadow-xl" data-id="element-1426">
            <div className="p-10 md:p-16 flex-1 z-10 flex items-center gap-8" data-id="element-1427">
              <div className="text-6xl sm:text-8xl font-bold text-white" data-id="element-1428">
                25<span className="text-4xl sm:text-6xl" data-id="element-1429">%</span>
              </div>
              <div className="w-1 h-24 bg-[#00BFA5] hidden sm:block" data-id="element-1430"></div>
              <p className="text-white text-lg sm:text-xl font-medium max-w-sm leading-snug" data-id="element-1431">
                A Georgetown study found that an associate degree can increase
                lifetime earnings over a high school diploma by 25 percent.⁶
              </p>
            </div>
            <div className="w-full md:w-1/2 h-64 md:h-full absolute right-0 top-0 opacity-80 md:opacity-100" data-id="element-1432">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Graduate smiling" className="w-full h-full object-cover object-right" data-id="element-1433" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#003B5C] to-transparent" data-id="element-1434"></div>
            </div>
          </div>
        </div>
      </section>

      {/* G. Testimonial Section */}
      <section className="bg-[#176B9C] py-16 relative" data-id="element-1435">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1436">
          <div className="text-[#00BFA5] text-6xl font-serif leading-none mb-4" data-id="element-1437">
            "
          </div>
          <p className="text-white text-xl sm:text-2xl font-medium leading-relaxed mb-8" data-id="element-1438">
            "I'm a working mom and mother of four. I am thankful to UMA for the
            flexibility, so I am able to complete my courses and pass with
            honors!"
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-1439">
            <div className="text-right" data-id="element-1440">
              <div className="text-white font-bold text-lg" data-id="element-1441">Merelesita R.</div>
              <div className="text-white/80 text-sm" data-id="element-1442">UMA Graduate</div>
            </div>
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/20" data-id="element-1443">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Merelesita R." className="w-full h-full object-cover" data-id="element-1444" />
            </div>
          </div>
        </div>
        {/* Triangle pointer */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[16px] border-t-[#176B9C]" data-id="element-1445"></div>
      </section>

      {/* H. Accreditation Bar */}
      <section className="bg-[#F8F9FA] py-12 mt-4" data-id="element-1446">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center gap-6" data-id="element-1447">
          <div className="w-20 h-20 flex-shrink-0 bg-white rounded-full p-2 shadow-sm flex items-center justify-center border border-gray-100" data-id="element-1448">
            <AwardIcon className="w-10 h-10 text-[#003B5C]" data-id="element-1449" />
          </div>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1450">
            UMA is institutionally accredited by the Accrediting Bureau of
            Health Education Schools (ABHES.org), a national accrediting agency
            that is recognized by the U.S. Secretary of Education. Ultimate
            Medical Academy does not recruit or enroll residents of the European
            Union.
          </p>
        </div>
      </section>

      {/* I. Citations Section */}
      <section className="bg-[#F8F9FA] py-8 border-t border-gray-200" data-id="element-1451">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1452">
          <h4 className="text-xs font-bold text-gray-800 mb-3" data-id="element-1453">Citations</h4>
          <ol className="text-[10px] text-gray-500 space-y-1 list-decimal list-inside" data-id="element-1454">
            <li data-id="element-1455">
              Coursework is online except for on-site externship/professional
              practice experience. Set schedules required for on-site
              externship/professional practice experience courses in select
              programs and coursework deadlines are set by instructors.
            </li>
            <li data-id="element-1456">
              Learners must meet eligibility criteria to sit for applicable
              certification exams
            </li>
            <li data-id="element-1457">
              Tuition considerations and credit transfer decisions are at the
              discretion of the receiving institution.
            </li>
            <li data-id="element-1458">
              For full information on laptop program eligibility, please see the
              UMA Catalog.
            </li>
            <li data-id="element-1459">
              Source: Survey was conducted by Hanover Research. Employers that
              hired UMA Online students between Jul 1, 2022 - September 30,
              2023. Survey responses received from 257 employers. Excludes
              corporate alliance partners.
            </li>
            <li data-id="element-1460">
              Georgetown University. The College Payoff. Study found that an
              associate degree can increase lifetime earnings over a high school
              diploma by 25 percent.
            </li>
          </ol>
          <p className="text-[10px] text-gray-500 mt-4" data-id="element-1461">
            Where national data regarding earnings or employability within a
            particular field are cited, individual readers should take note that
            national averages may not accurately reflect the earnings of workers
            in their particular part of the country, and may include earners at
            all stages of their career, not just entry-level wages for recent
            graduates.
          </p>
          <p className="text-[10px] text-gray-500 mt-2" data-id="element-1462">
            Set schedules required for on-site externship / professional
            practice experience courses in select programs and in all programs,
            coursework deadlines are set by instructors.
          </p>
        </div>
      </section>

      {/* J. UMA Footer */}
      <footer className="bg-white py-12 border-t border-gray-200" data-id="element-1463">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center md:items-start gap-8" data-id="element-1464">
          <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-14 object-contain" data-id="element-1465" />
          <div className="text-center md:text-left text-xs text-gray-600" data-id="element-1466">
            <p className="mb-1" data-id="element-1467">
              © 2026 UMA Education, Inc. d/b/a Ultimate Medical Academy. All
              rights reserved.
            </p>
            <p className="mb-4" data-id="element-1468">
              A member of the Vocate Education Solutions System
            </p>
            <div className="flex items-center justify-center md:justify-start gap-2 text-[#176B9C]" data-id="element-1469">
              <a href="#" className="hover:underline" data-id="element-1470">
                Marketing Statistics
              </a>
              <span data-id="element-1471">|</span>
              <a href="#" className="hover:underline" data-id="element-1472">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}